import { useState, useCallback } from 'react';
import { ethers } from 'ethers';
import PredictionMarketABI from '../abi/PredictionMarket.json';

const CONTRACT_ADDRESS =
  process.env.REACT_APP_SMART_CONTRACT_ADDRESS ||
  '0xE88582edFEc4CFb3B1A3ABa5A79c55B8C1d770fc';

export interface Market {
  id: number;
  question: string;
  deadline: number;   // unix timestamp
  resolved: boolean;
  outcome: boolean;
  yesPool: string;    // ETH string
  noPool: string;     // ETH string
}

export type TxStatus = 'idle' | 'pending' | 'success' | 'error';

export interface TxState {
  status: TxStatus;
  hash: string | null;
  error: string | null;
}

function getProvider(): ethers.BrowserProvider {
  const eth = (window as any).ethereum;
  if (!eth) throw new Error('MetaMask not detected. Please install MetaMask.');
  return new ethers.BrowserProvider(eth);
}

async function getSignerAndContract() {
  const provider = getProvider();
  const signer = await provider.getSigner();
  const contract = new ethers.Contract(CONTRACT_ADDRESS, PredictionMarketABI, signer);
  return { signer, contract };
}

async function getReadContract() {
  const provider = getProvider();
  return new ethers.Contract(CONTRACT_ADDRESS, PredictionMarketABI, provider);
}

export function usePredictionMarket() {
  const [markets, setMarkets] = useState<Market[]>([]);
  const [loadingMarkets, setLoadingMarkets] = useState(false);
  const [tx, setTx] = useState<TxState>({ status: 'idle', hash: null, error: null });

  const resetTx = useCallback(() =>
    setTx({ status: 'idle', hash: null, error: null }), []);

  // ── Fetch all markets from the contract ────────────────────────────────────
  const fetchMarkets = useCallback(async () => {
    setLoadingMarkets(true);
    try {
      const contract = await getReadContract();
      const count: bigint = await contract.marketCount();
      const fetched: Market[] = [];
      for (let i = 0; i < Number(count); i++) {
        const m = await contract.getMarket(i);
        fetched.push({
          id: i,
          question: m.question,
          deadline: Number(m.deadline),
          resolved: m.resolved,
          outcome: m.outcome,
          yesPool: ethers.formatEther(m.yesPool),
          noPool: ethers.formatEther(m.noPool),
        });
      }
      setMarkets(fetched);
    } catch (err: any) {
      console.error('[usePredictionMarket] fetchMarkets error:', err);
    } finally {
      setLoadingMarkets(false);
    }
  }, []);

  // ── Admin: Create Market ───────────────────────────────────────────────────
  const createMarket = useCallback(
    async (question: string, deadlineTimestamp: number): Promise<boolean> => {
      setTx({ status: 'pending', hash: null, error: null });
      try {
        const { contract } = await getSignerAndContract();
        const txResp = await contract.createMarket(question, BigInt(deadlineTimestamp));
        setTx({ status: 'pending', hash: txResp.hash, error: null });
        await txResp.wait();
        setTx({ status: 'success', hash: txResp.hash, error: null });
        await fetchMarkets();
        return true;
      } catch (err: any) {
        setTx({ status: 'error', hash: null, error: err?.message ?? 'Unknown error' });
        return false;
      }
    },
    [fetchMarkets],
  );

  // ── User: Place Bet ────────────────────────────────────────────────────────
  const placeBet = useCallback(
    async (marketId: number, prediction: boolean, amountEth: string): Promise<boolean> => {
      setTx({ status: 'pending', hash: null, error: null });
      try {
        const { contract } = await getSignerAndContract();
        const value = ethers.parseEther(amountEth);
        const txResp = await contract.placeBet(BigInt(marketId), prediction, { value });
        setTx({ status: 'pending', hash: txResp.hash, error: null });
        await txResp.wait();
        setTx({ status: 'success', hash: txResp.hash, error: null });
        await fetchMarkets();
        return true;
      } catch (err: any) {
        setTx({ status: 'error', hash: null, error: err?.message ?? 'Unknown error' });
        return false;
      }
    },
    [fetchMarkets],
  );

  // ── Admin: Resolve Market ──────────────────────────────────────────────────
  const resolveMarket = useCallback(
    async (marketId: number, outcome: boolean): Promise<boolean> => {
      setTx({ status: 'pending', hash: null, error: null });
      try {
        const { contract } = await getSignerAndContract();
        const txResp = await contract.resolveMarket(BigInt(marketId), outcome);
        setTx({ status: 'pending', hash: txResp.hash, error: null });
        await txResp.wait();
        setTx({ status: 'success', hash: txResp.hash, error: null });
        await fetchMarkets();
        return true;
      } catch (err: any) {
        setTx({ status: 'error', hash: null, error: err?.message ?? 'Unknown error' });
        return false;
      }
    },
    [fetchMarkets],
  );

  // ── User: Claim Winnings ───────────────────────────────────────────────────
  const claimWinnings = useCallback(
    async (marketId: number): Promise<boolean> => {
      setTx({ status: 'pending', hash: null, error: null });
      try {
        const { contract } = await getSignerAndContract();
        const txResp = await contract.claimWinnings(BigInt(marketId));
        setTx({ status: 'pending', hash: txResp.hash, error: null });
        await txResp.wait();
        setTx({ status: 'success', hash: txResp.hash, error: null });
        return true;
      } catch (err: any) {
        setTx({ status: 'error', hash: null, error: err?.message ?? 'Unknown error' });
        return false;
      }
    },
    [],
  );

  // ── Get user's bet amount on a market (yes or no side) ────────────────────
  const getUserBet = useCallback(
    async (marketId: number, address: string, prediction: boolean): Promise<string> => {
      try {
        const contract = await getReadContract();
        const amount: bigint = await contract.bets(
          BigInt(marketId),
          address,
          prediction,
        );
        return ethers.formatEther(amount);
      } catch (err: any) {
        console.debug('[usePredictionMarket] getUserBet error:', err?.message);
        return '0';
      }
    },
    [],
  );

  return {
    markets,
    loadingMarkets,
    fetchMarkets,
    createMarket,
    placeBet,
    resolveMarket,
    claimWinnings,
    getUserBet,
    tx,
    resetTx,
  };
}
